import { AccountQRCodePreferences } from '@app/domain/user/components/AccountQRCodePreferences';
import { TranslationKey } from '@app/locales/defaultTranslation';
import { useTranslation } from 'react-i18next';
import { GlobalSettingsBanner } from './GlobalSettingsBanner';
import { Button, Container, Input, OptionItem, Select, Typography } from '@app/components';
import { Controller, useForm } from 'react-hook-form';
import { Routes } from '@app/constants/routes';
import { useTypedParams } from '@app/router';
import { ShortURLWarningBanner } from './ShortURLWarningBanner';
import { Divider, Radio } from '@mui/material';
import { ArrowUpSquareIcon } from '@app/assets';
import { useStore } from '@app/store/useStore.hook';
import { useUpdateCustomer } from '@app/domain/user/api/user.hooks.api';
import { useEffect, useState } from 'react';
import { replaceSubdomain } from '../../qr-code.utils';
import { usePlanLimits } from '@app/domain/plan/hooks/usePlanLimits';
import { toast } from 'react-toastify';
import { PermissionCrown } from '@app/domain/plan/components/permission-crown/PermissionCrown';
import { useRefreshAccountOnPageFocus } from '@app/domain/user/hooks/useRefreshAccountOnPageFocus';
import { typedEnv } from '@app/environment/typed-env';

const GPSGlobalSettingsTitle: TranslationKey = 'qr.settings.global.qpsTitle';

enum EUserSubdomain {
  FAST_SUBDOMAIN = 'FAST_SUBDOMAIN',
  CUSTOM_SUBDOMAIN = 'CUSTOM_SUBDOMAIN',
}

export const QRSettingsGlobalTab: React.FC = () => {
  const { t } = useTranslation();
  const { canUseSubdomain } = usePlanLimits();
  useRefreshAccountOnPageFocus();

  const { currentUser: user } = useStore(({ currentUser }) => ({
    currentUser,
  }));
  const isSubdomainEnabled = user?.isSubdomainEnabled;
  const subdomain = user?.subdomain;
  const { id } = useTypedParams(Routes.customer.my_qr_codes.edit);
  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      shortUrlPreference: isSubdomainEnabled ? EUserSubdomain.CUSTOM_SUBDOMAIN : EUserSubdomain.FAST_SUBDOMAIN,
      qrCodeId: id,
    },
  });

  const { mutate: updateCustomer, isLoading } = useUpdateCustomer();

  const [defaultDomain] = useState(() => typedEnv.REACT_APP_SCAN_HOST_URL.replace('https://', ''));
  const [customDomain, setCustomDomain] = useState<string | null>(null);

  const shortUrlPreference = watch('shortUrlPreference');

  useEffect(() => {
    if (subdomain) {
      setCustomDomain(replaceSubdomain(subdomain, true).slice(0, -1));
    }
    if (!subdomain) {
      setValue('shortUrlPreference', EUserSubdomain.FAST_SUBDOMAIN);
    }
  }, [subdomain, setValue]);

  useEffect(() => {
    setValue(
      'shortUrlPreference',
      isSubdomainEnabled ? EUserSubdomain.CUSTOM_SUBDOMAIN : EUserSubdomain.FAST_SUBDOMAIN
    );
  }, [isSubdomainEnabled, setValue]);

  const handlerCancelSubdomainSelect = () => {
    setValue(
      'shortUrlPreference',
      isSubdomainEnabled ? EUserSubdomain.CUSTOM_SUBDOMAIN : EUserSubdomain.FAST_SUBDOMAIN
    );
  };

  const handlerSubdomainChange = async () => {
    const newValue = shortUrlPreference === EUserSubdomain.CUSTOM_SUBDOMAIN;

    if (isSubdomainEnabled === newValue) {
      return;
    }

    await updateCustomer(
      { isSubdomainEnabled: newValue },
      {
        onSuccess: () => {
          toast.success(t('toaster.changesSaved'));
        },
      }
    );
  };

  const renderShortUrlOption = (selected: EUserSubdomain) => {
    if (selected === EUserSubdomain.FAST_SUBDOMAIN) {
      return (
        <span className="whitespace-nowrap">
          {t('qr.settings.global.shortURL.select.defulteLabel')} ({defaultDomain})
        </span>
      );
    }
    if (selected === EUserSubdomain.CUSTOM_SUBDOMAIN) {
      return (
        <span className="whitespace-nowrap">
          {t('qr.settings.global.shortURL.select.customLabel')} ({customDomain})
        </span>
      );
    }
    return <span>Option {selected}</span>;
  };

  const isSameSubomain = isSubdomainEnabled === (shortUrlPreference === EUserSubdomain.CUSTOM_SUBDOMAIN);

  return (
    <div className="flex flex-col gap-4">
      <GlobalSettingsBanner />
      <Container className="flex flex-col gap-4 xl:rounded-xl">
        <Typography variant="m" className="font-bold flex items-center gap-2">
          {t('qr.settings.global.shortURL.title')}
          <PermissionCrown requireUpgrade={!canUseSubdomain} />
        </Typography>
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Controller
              name={'shortUrlPreference'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  onChange={onChange}
                  displayEmpty
                  disabled={!canUseSubdomain}
                  inputProps={{ label: t('qr.settings.global.shortURL.inputLabel') }}
                  renderValue={renderShortUrlOption}
                >
                  <OptionItem
                    value={EUserSubdomain.FAST_SUBDOMAIN}
                    selected={value === EUserSubdomain.FAST_SUBDOMAIN}
                    className="focus-within::!bg-gray-100 !bg-transparent hover:!bg-gray-100 focus:!bg-gray-100"
                  >
                    <div className="flex gap-2 items-start">
                      <Radio className="-mt-2" checked={value === EUserSubdomain.FAST_SUBDOMAIN} />
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-800 text-s">
                          {t('qr.settings.global.shortURL.select.defulteLabel')}
                        </span>
                        <span className="text-gray-800 italic text-s">{defaultDomain}</span>
                      </div>
                    </div>
                  </OptionItem>
                  <Divider className="my-1" />
                  <OptionItem
                    value={EUserSubdomain.CUSTOM_SUBDOMAIN}
                    disabled={!subdomain || !canUseSubdomain}
                    selected={value === EUserSubdomain.CUSTOM_SUBDOMAIN}
                    className="focus-within:!bg-gray-100 !bg-transparent hover:!bg-gray-100 focus:!bg-gray-100"
                  >
                    <div className="flex gap-2 items-start">
                      <Radio className="-mt-2" checked={value === EUserSubdomain.CUSTOM_SUBDOMAIN} />
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-800 text-s">
                          {t('qr.settings.global.shortURL.select.customLabel')}
                        </span>
                        {subdomain && <span className="text-gray-800 italic text-s">{customDomain}</span>}
                        {!subdomain && (
                          <span className="max-w-56 text-gray-800 italic text-s whitespace-break-spaces">
                            {t('qr.settings.global.shortURL.select.customDisabledMessage')}
                          </span>
                        )}
                      </div>
                    </div>
                  </OptionItem>
                  <div className="group mx-2 w-full flex mt-1">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(Routes.customer.account.white_label, '_blank');
                      }}
                      variant="outlined"
                      size="small"
                      className="w-[calc(100%-1rem)]"
                      endIcon={
                        <ArrowUpSquareIcon className="text-gray-800 group-hover:text-accept-hover transition-colors" />
                      }
                    >
                      {t('qr.settings.global.shortURL.whiteLinkButton')}
                    </Button>
                  </div>
                </Select>
              )}
            />
            <Controller
              name="qrCodeId"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={id}
                  label={t('qr.settings.global.shortURL.idLabel')}
                  className="disabled:opacity-100"
                  withBG
                  placeholder={t('qr.settings.global.shortURL.idLabel')}
                  disabled
                />
              )}
            />
          </div>
          <ShortURLWarningBanner withWrapper={!!user?.subdomain && !isSameSubomain}>
            <div className="flex gap-4">
              <Button
                disabled={isSameSubomain}
                onClick={handlerCancelSubdomainSelect}
                color="secondary"
                variant="outlined"
                className="w-[220px]"
              >
                {t('qr.buttonCancel')}
              </Button>
              <Button
                disabled={isSameSubomain}
                loading={isLoading}
                className="w-[220px]"
                onClick={handleSubmit(handlerSubdomainChange)}
              >
                {t('qr.settings.global.shortURL.chandeURLButton')}
              </Button>
            </div>
          </ShortURLWarningBanner>
        </div>
      </Container>
      <AccountQRCodePreferences title={GPSGlobalSettingsTitle} />
    </div>
  );
};
